import React, { useEffect, useMemo, useState } from 'react';
import {
  serviceFeeManagementApi,
  type FeeDetailsImportPreviewItem,
  type FeeDetailsImportPreviewResult,
} from '../../api/serviceFeeManagementApi';
import toast from 'react-hot-toast';

interface FeeDetailsImportModalProps {
  isOpen: boolean;
  buildingId: string;
  buildingName: string;
  billingPeriod: string;
  feeCategoryId?: string;
  feeCategoryName?: string;
  onClose: () => void;
  onImported: () => void;
}

type ModalStep = 'upload' | 'preview';
type PreviewFilter = 'all' | 'importable' | 'skipped';

const formatCurrency = (value: number) =>
  `${new Intl.NumberFormat('vi-VN').format(Math.round(value))}đ`;

const statusLabel: Record<FeeDetailsImportPreviewItem['status'], string> = {
  ready: 'Cập nhật',
  will_create: 'Tạo mới',
  unchanged: 'Không đổi',
  skipped: 'Bỏ qua',
};

const statusClass: Record<FeeDetailsImportPreviewItem['status'], string> = {
  ready: 'bg-green-100 text-green-800',
  will_create: 'bg-blue-100 text-blue-800',
  unchanged: 'bg-gray-100 text-gray-700',
  skipped: 'bg-amber-100 text-amber-800',
};

const importableCountFrom = (result: FeeDetailsImportPreviewResult) =>
  result.readyCount + result.willCreateCount + (result.surplusResyncCount ?? 0);

const resolveItemStatusLabel = (item: FeeDetailsImportPreviewItem) => {
  if (item.surplusResync) return 'Đồng bộ thu dư';
  return statusLabel[item.status];
};

const resolveItemStatusClass = (item: FeeDetailsImportPreviewItem) => {
  if (item.surplusResync) return 'bg-cyan-100 text-cyan-800';
  return statusClass[item.status];
};

export const FeeDetailsImportModal: React.FC<FeeDetailsImportModalProps> = ({
  isOpen,
  buildingId,
  buildingName,
  billingPeriod,
  feeCategoryId,
  feeCategoryName,
  onClose,
  onImported,
}) => {
  const [step, setStep] = useState<ModalStep>('upload');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<FeeDetailsImportPreviewResult | null>(null);
  const [previewFilter, setPreviewFilter] = useState<PreviewFilter>('importable');
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setStep('upload');
      setImportFile(null);
      setPreview(null);
      setPreviewFilter('importable');
      setError(null);
    }
  }, [isOpen]);

  const filteredItems = useMemo(() => {
    if (!preview) return [];
    if (previewFilter === 'all') return preview.items;
    if (previewFilter === 'importable') {
      return preview.items.filter(
        (item) =>
          item.status === 'ready' ||
          item.status === 'will_create' ||
          item.surplusResync,
      );
    }
    return preview.items.filter((item) => item.status === 'skipped');
  }, [preview, previewFilter]);

  const importableCount =
    (preview?.readyCount ?? 0) +
    (preview?.willCreateCount ?? 0) +
    (preview?.surplusResyncCount ?? 0);

  if (!isOpen) return null;

  const handleDownloadSample = async () => {
    try {
      setSampleLoading(true);
      setError(null);
      if (!feeCategoryId) {
        toast.error('Chọn một loại phí (tab) trước khi tải file mẫu');
        return;
      }
      await serviceFeeManagementApi.downloadFeeDetailsSample(
        buildingId,
        billingPeriod,
        feeCategoryId,
      );
      toast.success('Đã tải file mẫu');
    } catch {
      setError('Không thể tải file mẫu');
    } finally {
      setSampleLoading(false);
    }
  };

  const handlePreview = async (file: File) => {
    try {
      setPreviewLoading(true);
      setError(null);
      const result = await serviceFeeManagementApi.previewFeeDetailsImport(
        buildingId,
        billingPeriod,
        file,
      );
      setPreview(result);
      setPreviewFilter(importableCountFrom(result) > 0 ? 'importable' : 'skipped');
      setStep('preview');
      if (importableCountFrom(result) === 0 && result.errors.length > 0) {
        setError('Không có hóa đơn nào sẽ được nhập. Kiểm tra lỗi bên dưới.');
      } else if (importableCountFrom(result) === 0) {
        setError('Không có thay đổi nào trong file so với dữ liệu hiện tại.');
      }
    } catch (err: any) {
      setPreview(null);
      setStep('upload');
      setError(err.response?.data?.message || 'Không thể đọc file');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleFileChange = (file: File | null) => {
    setImportFile(file);
    setPreview(null);
    setStep('upload');
    setError(null);
    if (file) {
      void handlePreview(file);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      setError('Vui lòng chọn file Excel');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const result = await serviceFeeManagementApi.importFeeDetails(
        buildingId,
        billingPeriod,
        importFile,
      );
      if (result.errors.length > 0) {
        toast.error(
          `Nhập ${result.updatedCount + (result.createdCount ?? 0)} dòng, đồng bộ thu dư ${result.surplusSyncedCount ?? 0}, bỏ qua ${result.skippedCount}. ${result.errors[0]}`,
        );
      } else {
        const synced = result.surplusSyncedCount ?? 0;
        toast.success(
          `Đã nhập ${result.updatedCount + (result.createdCount ?? 0)} hóa đơn` +
            ((result.createdCount ?? 0) > 0 ? ` (${result.createdCount} tạo mới)` : '') +
            (synced > 0 ? `, đồng bộ thu dư ${synced} căn hộ` : ''),
        );
      }
      setImportFile(null);
      onImported();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Không thể nhập file');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Nhập chi tiết phí</h2>
            <p className="text-sm text-gray-500 mt-1">
              Nợ đầu kỳ kỳ <strong>{billingPeriod}</strong> — {buildingName}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-200 rounded-full"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {step === 'upload' && (
            <>
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-700 mb-3">Bước 1 — Tải file mẫu</h3>
                <p className="text-gray-600 mb-3 text-sm leading-relaxed">
                  <strong>Kỳ thu phí đang chọn: {billingPeriod}</strong>
                  {feeCategoryName ? (
                    <>
                      {' '}
                      — loại phí: <strong>{feeCategoryName}</strong>. File mẫu liệt kê{' '}
                      <strong>tất cả căn hộ</strong> của chung cư cho loại phí này; điền cột{' '}
                      <strong>PHÁT SINH</strong> rồi nhập lại.
                    </>
                  ) : (
                    <>
                      {' '}
                      — chọn tab <strong>loại phí</strong> (ví dụ Phí vận chuyển rác) trước khi tải
                      mẫu.
                    </>
                  )}
                </p>
                <p className="text-gray-600 mb-3 text-sm leading-relaxed">
                  Kỳ trên file là <strong>T. THU PHÍ</strong> / kỳ hóa đơn (theo loại phí). Với phí
                  thu cùng kỳ thông báo, thường trùng kỳ đang chọn ({billingPeriod}).
                </p>
                <p className="text-gray-600 mb-3 text-sm leading-relaxed">
                  Nhập file công nợ nhận bàn giao (1 dòng/căn hộ): hệ thống tạo loại phí{' '}
                  <strong>Công nợ nhận bàn giao (CNNBG)</strong>, map <strong>Còn nợ → Nợ đầu kỳ</strong>{' '}
                  kỳ {billingPeriod}.
                </p>
                <button
                  type="button"
                  onClick={handleDownloadSample}
                  disabled={sampleLoading}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                >
                  {sampleLoading ? 'Đang tải...' : 'Tải file mẫu'}
                </button>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-700 mb-3">Bước 2 — Chọn file Excel</h3>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                    className="hidden"
                    id="fee-details-import-file"
                  />
                  <label
                    htmlFor="fee-details-import-file"
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    <span className="text-gray-600 text-sm">
                      {importFile ? importFile.name : 'Nhấn để chọn file Excel'}
                    </span>
                    <span className="text-xs text-gray-500">Định dạng .xlsx hoặc .xls</span>
                  </label>
                </div>
                {previewLoading && (
                  <p className="text-sm text-blue-600 mt-3 text-center">Đang kiểm tra file...</p>
                )}
              </div>

              <div className="bg-gray-50 p-4 rounded-lg text-sm space-y-2">
                <p>
                  <strong className="text-red-600">*</strong> MÃ CĂN HỘ, LOẠI PHÍ — khớp hóa đơn hiện có
                </p>
                <p>
                  • File công nợ cũ: <strong>Còn nợ</strong> → <strong>Nợ đầu kỳ kỳ {billingPeriod}</strong>{' '}
                  (CNNBG); <strong>Thu dư</strong> vào tài khoản căn hộ
                </p>
                <p>• File mẫu EBMS: cập nhật NỢ ĐẦU KỲ / ĐÃ THANH TOÁN / NỢ CUỐI KỲ theo loại phí</p>
                <p>• <strong>Nợ đầu kỳ âm</strong> → chuyển sang <strong>Thu dư</strong> (Tài khoản căn hộ)</p>
                <p>• Sau khi chọn file, hệ thống hiển thị xem trước trước khi áp dụng</p>
              </div>
            </>
          )}

          {step === 'preview' && preview && (
            <>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-medium text-gray-800">Xem trước kết quả</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {preview.formatLabel} — {importFile?.name}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setStep('upload');
                    setPreview(null);
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  ← Chọn file khác
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
                <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                  <div className="text-xs text-green-700">Cập nhật</div>
                  <div className="text-xl font-bold text-green-800">{preview.readyCount}</div>
                </div>
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                  <div className="text-xs text-blue-700">Tạo mới</div>
                  <div className="text-xl font-bold text-blue-800">{preview.willCreateCount}</div>
                </div>
                <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-3">
                  <div className="text-xs text-cyan-700">Đồng bộ thu dư</div>
                  <div className="text-xl font-bold text-cyan-800">{preview.surplusResyncCount ?? 0}</div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="text-xs text-gray-600">Không đổi</div>
                  <div className="text-xl font-bold text-gray-800">{preview.unchangedCount}</div>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="text-xs text-amber-700">Bỏ qua</div>
                  <div className="text-xl font-bold text-amber-800">{preview.skippedCount}</div>
                </div>
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                  <div className="text-xs text-indigo-700">Căn hộ</div>
                  <div className="text-xl font-bold text-indigo-800">{preview.apartmentCount}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 text-sm">
                <div className="rounded-lg border border-gray-200 p-3">
                  <div className="font-medium text-gray-700 mb-1">Nợ đầu kỳ (tổng thay đổi)</div>
                  <div className="text-gray-600">
                    {formatCurrency(preview.summary.openingDebtBefore)} →{' '}
                    <span className="font-semibold text-gray-900">
                      {formatCurrency(preview.summary.openingDebtAfter)}
                    </span>
                  </div>
                </div>
                <div className="rounded-lg border border-gray-200 p-3">
                  <div className="font-medium text-gray-700 mb-1">Đã thanh toán (tổng thay đổi)</div>
                  <div className="text-gray-600">
                    {formatCurrency(preview.summary.paidAmountBefore)} →{' '}
                    <span className="font-semibold text-gray-900">
                      {formatCurrency(preview.summary.paidAmountAfter)}
                    </span>
                  </div>
                </div>
                {preview.summary.surplusBalance > 0 && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                    <div className="font-medium text-emerald-800 mb-1">Thu dư</div>
                    <div className="font-semibold text-emerald-900">
                      {formatCurrency(preview.summary.surplusBalance)}
                    </div>
                  </div>
                )}
              </div>

              {preview.errors.length > 0 && (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
                  <div className="font-medium text-amber-800 mb-2">Lỗi / cảnh báo ({preview.errors.length})</div>
                  <ul className="space-y-1 text-amber-900 max-h-24 overflow-y-auto">
                    {preview.errors.map((message) => (
                      <li key={message}>• {message}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex flex-wrap gap-2 mb-3">
                {([
                  ['importable', `Sẽ nhập (${importableCount})`],
                  ['skipped', `Bỏ qua (${preview.skippedCount})`],
                  ['all', `Tất cả (${preview.items.length})`],
                ] as const).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setPreviewFilter(key)}
                    className={`px-3 py-1.5 rounded-full text-sm ${
                      previewFilter === key
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {preview.totalRows > preview.items.length && (
                <p className="text-xs text-gray-500 mb-2">
                  Hiển thị {preview.items.length}/{preview.totalRows} dòng đầu tiên trong bảng xem trước.
                </p>
              )}

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-72">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Căn hộ</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Loại phí</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Trạng thái</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-600">Nợ đầu kỳ</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-600">Đã TT</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-600">Nợ cuối kỳ</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-600">Thu dư</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredItems.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                            Không có dòng nào trong bộ lọc này.
                          </td>
                        </tr>
                      ) : (
                        filteredItems.map((item, index) => (
                          <tr key={`${item.apartmentCode}-${item.feeCategoryName}-${index}`} className="border-t">
                            <td className="px-3 py-2 whitespace-nowrap">{item.apartmentCode}</td>
                            <td className="px-3 py-2">{item.feeCategoryName}</td>
                            <td className="px-3 py-2">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${resolveItemStatusClass(item)}`}>
                                {item.reason ? item.reason : resolveItemStatusLabel(item)}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              {item.status === 'ready' && item.openingDebtBefore !== item.openingDebtAfter ? (
                                <>
                                  <span className="text-gray-400 line-through mr-1">
                                    {formatCurrency(item.openingDebtBefore)}
                                  </span>
                                  {formatCurrency(item.openingDebtAfter)}
                                </>
                              ) : (
                                formatCurrency(item.openingDebtAfter)
                              )}
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              {item.status === 'ready' && item.paidAmountBefore !== item.paidAmountAfter ? (
                                <>
                                  <span className="text-gray-400 line-through mr-1">
                                    {formatCurrency(item.paidAmountBefore)}
                                  </span>
                                  {formatCurrency(item.paidAmountAfter)}
                                </>
                              ) : (
                                formatCurrency(item.paidAmountAfter)
                              )}
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              {formatCurrency(item.closingDebtAfter)}
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap text-emerald-600">
                              {formatCurrency(item.surplusBalanceAfter ?? 0)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
            Hủy
          </button>
          {step === 'preview' && (
            <button
              type="button"
              onClick={handleImport}
              disabled={loading || previewLoading || !importFile || !preview || importableCount === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? 'Đang áp dụng...' : `Áp dụng ${importableCount} dòng`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeeDetailsImportModal;
